<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            :defaultColDef="defaultColDef"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :getRowStyle="getRowStyle"
            :pinnedBottomRowData="pinnedBottomRowData"
            :isAutoWidth="true"
            :isSearch="false"
            gridTheme="ag-theme-alpine-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-button
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                :icon="['fas', 'file-excel']"
                variant="primary"
            ></responsive-button>
             <exportToPDFButton
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
            />
        </grid>
    </page>
</template>

<script>
import API from '../../api';
import grid from '../generic/grid/grid.vue';
import page from '../generic/page/page.vue';
import format from 'date-fns/format';
import { REPORT_ISSUED_POLICIES_ENDPOINT } from '../../endpoints';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    TOMORROW,
    THIS_WEEK,
    NEXT_WEEK,
    NEXT_MONTH,
    THIS_YEAR,
    LAST_WEEK,
} from '../generic/date-range-picker/date-range-picker.vue';
import { mapGetters } from 'vuex';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import { defineAsyncComponent } from 'vue';

const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    components: {
        page,
        grid,
        DateRangePicker,
        ResponsiveButton,
        exportToPDFButton,
    },
    data() {
        return {
            busy: null,
            dateRange: nameToRange(THIS_MONTH),
            dateRequestFormat: 'yyyy-MM-dd',
            end_date: null,
            defaultColDef: {
                minWidth: 80,
            },
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR
            ],
            rowData: [],
            start_date: null,
            tabs: [
                {
                    title: 'Issued Policies',
                    route: '/app/reports/issued_policies/',
                    active: true,
                },
            ],
            total: null,
            pinnedBottomRowData: null,
            getRowStyle: null,
            totalRecords: 0,
            pdfCacheKey: null,
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        columnDefs() {
            return [
                {
                    headerName: '#',
                    valueGetter: (params) => {
                        return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1
                    },
                    minWidth: 40,
                },
                {
                    field: 'created',
                    headerName: 'Created',
                    minWidth: 100,
                    type: 'dateColumn',
                },
                {
                    field: 'istatus',
                    headerName: 'I. Status',
                    cellStyle: (params) => {
                        const { data } = params;
                        if (data.status !== 'ACTIVE') {
                            return { color: 'orange' };
                        }
                        return null;
                    },
                },
                {
                    field: 'pstatus',
                    headerName: 'P. Status',
                    cellStyle: (params) => {
                        const { data } = params;
                        if (data.status !== 'ACTIVE') {
                            return { color: 'orange' };
                        }
                        return null;
                    },
                },
                {
                    field: 'product_type',
                    headerName: 'Type',
                },
                {
                    field: 'invoice_pk',
                    headerName: 'Invoice #',
                },
                {
                    field: 'subscriber',
                    headerName: 'Subscriber',
                },
                {
                    field: 'policyholder',
                    headerName: 'Policyholder',
                },
                {
                    field: 'policy_id',
                    headerName: 'Policy ID',
                },
                {
                    field: 'check_in',
                    headerName: 'Check-In',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'check_out',
                    headerName: 'Check-Out',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'retail_cost',
                    headerName: 'Retail',
                    type: 'amountColumn',
                    cellClass: 'ag-left-aligned-cell',
                    headerClass: 'ag-left-aligned-header',
                    cellStyle: (params) => {
                        if (params.value < 0) {
                            return { color: 'orange' };
                        }
                        return null;
                    },
                },
                {
                    field: 'wholesale_cost',
                    headerName: 'Wholesale',
                    type: 'amountColumn',
                    cellClass: 'ag-left-aligned-cell',
                    headerClass: 'ag-left-aligned-header',
                    cellStyle: (params) => {
                        if (params.value < 0) {
                            return { color: 'orange' };
                        }
                        return null;
                    },
                    autoWidthExclude: true
                },
            ];
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        exportPdfHref() {
            return `/app/reports/issued-policies/row-data/pdf/?start_date=${this.startDateRequest}&end_date=${this.endDateRequest}`;
        },
        exportExcelHref() {
            return `/app/reports/issued-policies/row-data/excel/?start_date=${this.startDateRequest}&end_date=${this.endDateRequest}`;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
    },        
    beforeMount() {
        this.getRowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        this.pinnedBottomRowData = [
            {
                'wholesale_cost': 0,
                'retail_cost': 0,
            }
        ];
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_ISSUED_POLICIES_ENDPOINT(),
                    {
                        params: {
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                        },
                    }
                );
                this.pdfCacheKey = data.pdf_cache_key
                this.rowData = data.policies.map((policy, index) => ({ id: index, ...policy }));
                this.totalRecords = data.policies.length;
                this.total = data.total;
                this.pinnedBottomRowData = [
                    {
                        'wholesale_cost': this.total.wholesale_sum || 0,
                        'retail_cost': this.total.retail_sum || 0,
                    }
                ];
            } catch {
                this.errorToast({ text: "Could not fetch data" });
            } finally {
                this.busy = false
            }
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
};
</script>
